import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LoaderCircle, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo, EASE } from "@/components/landing/Reveal";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer le message. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <header className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/"><Logo compact /></Link>
          <Link to="/" className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-bleu" data-testid="contact-back-home">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour à l'accueil
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-xl px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bleu-light"><Mail className="h-6 w-6 text-bleu" /></span>
          <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Nous contacter</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Une question sur la plateforme, un partenariat, un problème avec votre compte ?
            Écrivez-nous : nous vous répondrons à l'adresse indiquée.
          </p>

          {sent ? (
            <div className="card-integra mt-8 p-8 text-center" data-testid="contact-success">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50"><Send className="h-5 w-5 text-emeraude" /></span>
              <p className="mt-4 text-sm font-bold text-slate-900">Message envoyé !</p>
              <p className="mt-1 text-xs text-slate-500">Merci {form.name.split(" ")[0]}, notre équipe vous répondra rapidement à {form.email}.</p>
              <Link to="/" className="btn-primary mx-auto mt-6 !px-6 !py-2.5 !text-xs">Retour à l'accueil</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="card-integra mt-8 space-y-3 p-6 sm:p-8">
              <div className="grid gap-3 sm:grid-cols-2">
                <input required minLength={2} maxLength={120} value={form.name} onChange={set("name")} placeholder="Votre nom"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="contact-name-input" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Votre email"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="contact-email-input" />
              </div>
              <input required minLength={2} maxLength={160} value={form.subject} onChange={set("subject")} placeholder="Objet de votre message"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="contact-subject-input" />
              <textarea required minLength={10} maxLength={4000} rows={6} value={form.message} onChange={set("message")}
                placeholder="Votre message…"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none" data-testid="contact-message-input" />
              <button type="submit" disabled={sending} className="btn-primary w-full !py-3 disabled:opacity-50" data-testid="contact-submit">
                {sending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Envoyer mon message</>}
              </button>
              <p className="text-center text-[10px] text-slate-400">Votre message est transmis à admin@integrafrance.com.</p>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
}
